import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Star, AlertTriangle, CheckCircle2, EyeOff, ChevronDown, Loader2, Home, LayoutList, DoorOpen } from 'lucide-react';
import { matApi } from '../../api/materialthequeApi';
import { RoomMatrix } from './RoomMatrix';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const STATUS = {
  arbitrated: { label: 'Retenu', color: '#86EFAC', bg: 'rgba(34,197,94,0.14)', icon: CheckCircle2 },
  ambiguous:  { label: 'Plusieurs choix — l\u2019IA tranchera', color: '#FDE68A', bg: 'rgba(250,204,21,0.14)', icon: AlertTriangle },
  single:     { label: 'Un seul candidat', color: '#CBD5E1', bg: 'rgba(148,163,184,0.14)', icon: CheckCircle2 },
  unused:     { label: 'Non envoyé à l\u2019IA (non classé)', color: '#9CA3AF', bg: 'rgba(148,163,184,0.10)', icon: EyeOff },
  empty:      { label: 'Slot vide — matériau à renseigner', color: '#FCA5A5', bg: 'rgba(239,68,68,0.14)', icon: AlertTriangle },
};

/**
 * MaterialBriefBanner — V15.11 · « Ce que l'IA recevra ».
 * Par rôle : le matériau retenu (ou la liste ambiguë), un sélecteur pour
 * arbitrer en un clic, et les pièces d'affectation du retenu.
 */
export const MaterialBriefBanner = ({ projectId, refreshKey = 0, onChanged, readOnly = false }) => {
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(true);
  const [busyRole, setBusyRole] = useState(null);
  const [view, setView] = useState('roles'); // roles | rooms
  const [matrixKey, setMatrixKey] = useState(0);

  const load = useCallback(() => {
    if (!projectId) return;
    matApi.getBriefReport(projectId).then(setReport).catch(() => setReport(null));
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const retain = async (role, materialId) => {
    setBusyRole(role.subrole);
    try {
      if (materialId === '__none__') {
        const cur = role.candidates.find((c) => c.is_retained);
        if (cur) await matApi.retainMaterial(cur.material_id);
      } else {
        await matApi.retainMaterial(materialId);
      }
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Arbitrage impossible');
    } finally {
      setBusyRole(null);
    }
  };

  const fillSlot = async (role, materialId) => {
    if (!materialId || materialId === '__none__') return;
    setBusyRole(role.subrole);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/materialtheque/materials/${role.slot_id}/fill-from/${materialId}`, {}, { withCredentials: true });
      toast.success(`Modèle par défaut défini : ${r.data?.name}`);
      load(); setMatrixKey((k) => k + 1); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de renseigner le slot');
    } finally {
      setBusyRole(null);
    }
  };

  // V15.26 — « Tout reclasser » : aperçu puis application en une fois.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const allConflicts = (report?.roles || []).flatMap((r) => (r.conflicts || []).map((c) => ({ ...c, fromLabel: r.label })));
  const reclassifyAll = async () => {
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/materialtheque/projects/${projectId}/reclassify-conflicts`, {}, { withCredentials: true });
      toast.success(`${data.applied} matériau${data.applied > 1 ? 'x' : ''} reclassé${data.applied > 1 ? 's' : ''}`);
      setBulkOpen(false);
      load(); setMatrixKey((k) => k + 1); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reclassement groupé impossible');
    } finally {
      setBulkBusy(false);
    }
  };

  // V15.25 — reclassement en un clic : force le rôle suggéré (subrole_hint) sur le matériau.
  const reclassify = async (cand) => {
    setBusyRole(cand.material_id);
    try {
      await matApi.updateMaterial(cand.material_id, { subrole_hint: cand.conflict.suggested_subrole });
      toast.success(`« ${cand.name} » reclassé en ${cand.conflict.suggested_label}`);
      load(); setMatrixKey((k) => k + 1); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reclassement impossible');
    } finally {
      setBusyRole(null);
    }
  };

  const toggleRoom = async (cand, room) => {
    const rooms = cand.rooms.includes(room) ? cand.rooms.filter((r) => r !== room) : [...cand.rooms, room];
    try {
      await matApi.updateMaterial(cand.material_id, { rooms });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Affectation impossible');
    }
  };

  if (!report || report.total_roles === 0) return null;
  const sentRoles = report.roles.filter((r) => r.status !== 'unused');

  return (
    <div className="rounded-xl border mb-5 overflow-hidden" style={{ borderColor: 'var(--c-border)', background: 'rgba(255,255,255,0.025)' }}
         data-testid="material-brief-banner">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-5 py-3 text-left"
              data-testid="material-brief-toggle">
        <Star className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
        <div className="flex-1 min-w-0">
          <span className="hud-eyebrow hud-eyebrow-gold">Ce que l&apos;IA recevra</span>
          <p className="text-[13px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
            {sentRoles.length} rôle{sentRoles.length > 1 ? 's' : ''} ·{' '}
            <span style={{ color: '#86EFAC' }}>{report.arbitrated_count} retenu{report.arbitrated_count > 1 ? 's' : ''}</span>
            {report.ambiguous_count > 0 && (
              <> · <span style={{ color: '#FDE68A' }} data-testid="material-brief-ambiguous-count">{report.ambiguous_count} à arbitrer</span></>
            )}
            {report.empty_count > 0 && (
              <> · <span style={{ color: '#FCA5A5' }} data-testid="material-brief-empty-count">{report.empty_count} slot{report.empty_count > 1 ? 's' : ''} vide{report.empty_count > 1 ? 's' : ''}</span></>
            )}
            {report.conflict_count > 0 && (
              <> · <span style={{ color: '#FB923C' }} data-testid="material-brief-conflict-count">{report.conflict_count} rôle{report.conflict_count > 1 ? 's' : ''} incohérent{report.conflict_count > 1 ? 's' : ''}</span>
                {!readOnly && (
                  <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setBulkOpen(true); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setBulkOpen(true); } }}
                        className="ml-2 inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded font-semibold"
                        style={{ background: '#FB923C', color: '#1a1206' }} data-testid="material-brief-reclassify-all-btn">
                    <AlertTriangle className="w-3 h-3" /> Tout reclasser
                  </span>
                )}</>
            )}
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} style={{ color: 'var(--c-text-muted)' }} />
      </button>
      {open && (
        <>
          <div className="flex items-center gap-1 px-5 pb-2" role="tablist" data-testid="material-brief-views">
            {[{ id: 'roles', label: 'Par rôle', icon: LayoutList }, { id: 'rooms', label: 'Par pièce', icon: DoorOpen }].map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={view === t.id} onClick={() => setView(t.id)}
                      className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border transition-colors"
                      style={view === t.id ? { background: 'rgba(217,160,91,0.16)', borderColor: 'rgba(217,160,91,0.6)', color: 'var(--c-gold)' }
                                           : { borderColor: 'var(--c-border)', color: 'var(--c-text-muted)' }}
                      data-testid={`material-brief-view-${t.id}`}>
                <t.icon className="w-3 h-3" /> {t.label}
              </button>
            ))}
            <span className="ml-2 text-[10.5px]" style={{ color: 'var(--c-text-muted)' }}>
              {view === 'roles' ? 'Un matériau retenu par rôle ; les slots vides acceptent un modèle par défaut.'
                                : 'Pièces extraites des lots du projet : chaque case doit être couverte. Ce tableau résolu (épinglé > retenu du rôle) est envoyé tel quel à l\'IA et prime sur les listes par rôle et sur le style de décoration.'}
            </span>
          </div>
          {view === 'roles' ? (
            <div className="divide-y border-t" style={{ borderColor: 'var(--c-border)' }}>
              {report.roles.map((role) => (
                <RoleRow key={`${role.family}:${role.subrole}`} role={role} rooms={report.project_rooms || []}
                         busy={busyRole === role.subrole} readOnly={readOnly} busyMaterial={busyRole}
                         onRetain={(id) => retain(role, id)} onToggleRoom={toggleRoom} onFillSlot={(id) => fillSlot(role, id)} onReclassify={reclassify} />
              ))}
            </div>
          ) : (
            <div className="border-t" style={{ borderColor: 'var(--c-border)' }}>
              <RoomMatrix projectId={projectId} refreshKey={matrixKey + refreshKey} onChanged={() => { load(); onChanged?.(); }} />
            </div>
          )}
        </>
      )}
      <Dialog open={bulkOpen} onOpenChange={(v) => { if (!bulkBusy) setBulkOpen(v); }}>
        <DialogContent className="max-w-2xl" style={{ background: 'var(--c-surface-0)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }} data-testid="reclassify-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]"><AlertTriangle className="w-4 h-4" style={{ color: '#FB923C' }} /> Tout reclasser — {allConflicts.length} matériau{allConflicts.length > 1 ? 'x' : ''}</DialogTitle>
            <DialogDescription className="text-[11.5px]" style={{ color: 'var(--c-text-2)' }}>Aperçu des changements de rôle. Chaque matériau sera forcé dans le rôle suggéré ; vous pourrez toujours le re-déplacer individuellement.</DialogDescription>
          </DialogHeader>
          <ul className="max-h-[55vh] overflow-auto divide-y" style={{ borderColor: 'var(--c-border)' }} data-testid="reclassify-all-list">
            {allConflicts.map((c) => (
              <li key={c.material_id} className="py-1.5 text-[11.5px] flex items-center gap-2 flex-wrap" data-testid={`reclassify-all-item-${c.material_id}`}>
                <span className="font-medium truncate max-w-[300px]" style={{ color: 'var(--c-text)' }}>{c.name}</span>
                <span style={{ color: 'var(--c-text-faint)' }}>{c.fromLabel}</span>
                <span style={{ color: '#FB923C' }}>→</span>
                <span style={{ color: '#FDBA74' }}>{c.conflict.suggested_label}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setBulkOpen(false)} disabled={bulkBusy} className="text-[12px] px-3 py-1.5 rounded-lg border" style={{ borderColor: 'var(--c-border)', color: 'var(--c-text-2)' }} data-testid="reclassify-all-cancel">Annuler</button>
            <button type="button" onClick={reclassifyAll} disabled={bulkBusy || allConflicts.length === 0} className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50" style={{ background: '#FB923C', color: '#1a1206' }} data-testid="reclassify-all-confirm">
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Appliquer les {allConflicts.length} reclassements
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RoleRow = ({ role, rooms, busy, readOnly, busyMaterial, onRetain, onToggleRoom, onFillSlot, onReclassify }) => {
  const st = STATUS[role.status] || STATUS.single;
  const Icon = st.icon;
  const retained = role.candidates.find((c) => c.is_retained) || null;
  const selectable = !['unused', 'empty'].includes(role.status) && role.candidates.length > 1;
  const conflicts = role.conflicts || [];
  return (
    <div className="px-5 py-2.5 flex items-start gap-3 flex-wrap" data-testid={`brief-role-${role.subrole}`}
         style={conflicts.length ? { background: 'rgba(251,146,60,0.06)', boxShadow: 'inset 3px 0 0 #FB923C' } : undefined}>
      {conflicts.length > 0 && (
        <div className="w-full flex flex-col gap-1" data-testid={`brief-role-conflicts-${role.subrole}`}>
          {conflicts.map((c) => (
            <div key={c.material_id} className="flex items-center gap-2 flex-wrap text-[11px] rounded-md px-2 py-1"
                 style={{ background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.45)', color: '#FDBA74' }}
                 data-testid={`brief-conflict-${c.material_id}`}>
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate max-w-[420px]"><strong style={{ color: 'var(--c-text)' }}>{c.name}</strong> est classé en « {role.label} » mais ressemble à : <strong>{c.conflict.suggested_label}</strong></span>
              {!readOnly && (
                <button type="button" onClick={() => onReclassify(c)} disabled={busyMaterial === c.material_id}
                        className="ml-auto inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded font-semibold disabled:opacity-50"
                        style={{ background: '#FB923C', color: '#1a1206' }} data-testid={`brief-reclassify-${c.material_id}`}>
                  {busyMaterial === c.material_id ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Reclasser en « {c.conflict.suggested_label} »
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="w-[230px] shrink-0">
        <div className="text-[12.5px] font-medium" style={{ color: 'var(--c-text)' }}>{role.label}</div>
        <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: st.bg, color: st.color }} data-testid={`brief-role-status-${role.subrole}`}>
          <Icon className="w-3 h-3" /> {st.label} · {role.candidates.length}
        </span>
      </div>
      <div className="flex-1 min-w-[260px]">
        {selectable && !readOnly ? (
          <div className="flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--c-gold)' }} />
                  : <Star className="w-3.5 h-3.5" style={{ color: retained ? 'var(--c-gold)' : 'var(--c-text-muted)', fill: retained ? 'var(--c-gold)' : 'none' }} />}
            <select
              value={retained?.material_id || '__none__'}
              onChange={(e) => onRetain(e.target.value)}
              disabled={busy}
              className="flex-1 bg-black/30 border rounded px-2 py-1 text-[12px] outline-none"
              style={{ borderColor: retained ? 'rgba(217,160,91,0.5)' : 'var(--c-border)', color: 'var(--c-text)' }}
              data-testid={`brief-role-select-${role.subrole}`}
            >
              <option value="__none__">— Aucun retenu : les {role.candidates.length} seront envoyés —</option>
              {role.candidates.map((c) => (
                <option key={c.material_id} value={c.material_id}>★ {c.name}{c.finish ? ` — ${c.finish}` : ''}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="text-[12px] truncate" style={{ color: 'var(--c-text-2)' }} title={role.sent_text}>
            {role.status === 'unused' ? <em>Ces {role.candidates.length} entrées ne sont pas utilisées dans le prompt.</em>
              : role.status === 'empty' ? (
                (role.suggestions || []).length > 0 && !readOnly ? (
                  <div className="flex items-center gap-2">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: '#FCA5A5' }} /> : <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#FCA5A5' }} />}
                    <select defaultValue="__none__" onChange={(e) => onFillSlot?.(e.target.value)} disabled={busy}
                            className="flex-1 bg-black/30 border rounded px-2 py-1 text-[12px] outline-none"
                            style={{ borderColor: 'rgba(239,68,68,0.45)', color: 'var(--c-text)' }}
                            data-testid={`brief-slot-default-select-${role.subrole}`}>
                      <option value="__none__">— Modèle par défaut : choisir un matériau du projet —</option>
                      {role.suggestions.map((c) => (
                        <option key={c.material_id} value={c.material_id}>{c.is_retained ? '★ ' : ''}{c.name}{c.finish ? ` — ${c.finish}` : ''}</option>
                      ))}
                    </select>
                  </div>
                ) : <em>Slot créé depuis le plan : aucun matériau compatible dans le projet — ajoutez-en (Enrichir / Ajouter) ou éditez le slot ci-dessous.</em>
              )
              : role.sent_text}
          </div>
        )}
        {retained && rooms.length > 0 && !readOnly && (
          <div className="mt-1.5 flex items-center gap-1 flex-wrap" data-testid={`brief-role-rooms-${role.subrole}`}>
            <Home className="w-3 h-3" style={{ color: 'var(--c-text-muted)' }} />
            <span className="text-[10px] mr-1" style={{ color: 'var(--c-text-muted)' }}>Pièces :</span>
            {rooms.map((r) => {
              const on = retained.rooms.includes(r);
              return (
                <button key={r} type="button" onClick={() => onToggleRoom(retained, r)}
                        className="text-[10px] px-1.5 py-0.5 rounded-full border transition-colors"
                        style={on ? { background: 'rgba(217,160,91,0.18)', borderColor: 'rgba(217,160,91,0.6)', color: 'var(--c-gold)' }
                                  : { borderColor: 'var(--c-border)', color: 'var(--c-text-muted)' }}
                        data-testid={`brief-room-chip-${role.subrole}-${r}`}>
                  {r}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialBriefBanner;
